/**
 * combo-cart.js — V2 single-writer + idempotent reconciliation
 * ============================================================================
 * BUILD: 2026-06-28-v2.14-kite-gift-marker+alloc-log
 *
 * One module owns every cart mutation. All actions go through a mutex queue.
 * State is DECLARATIVE: computeTargets() returns the correct absolute qty per
 * (combo, colour), then reconcile() applies it with ONE atomic /cart/update.js
 * keyed by variant_id (adds absent, removes at 0). Atomic ⇒ an interrupted
 * reconcile can never leave a phantom 2nd unit; and it's 1 round trip, not 3.
 *
 * THE ONE RULE (per combo group, per companion colour):
 *   net = min(totalUnits, anchorQty)  ; the rest are public(full price)
 * Bidirectional: anchor↑ upgrades public→net, anchor↓ downgrades net→public.
 *
 * NO line-item tags: companions are identified purely by variant membership, so
 * each companion variant is at most one line — addressable by variant_id. (A
 * future cross-price allocator assigns shared companions to the cheapest combo.)
 * ============================================================================
 */
console.log('%c[combo-cart] build=2026-06-28-v2.14-kite-gift-marker+alloc-log', 'color:#fff;background:#06c;padding:2px 6px;border-radius:3px');

(function () {
  const REGISTRY = Array.isArray(window.HULU_COMBO_REGISTRY) ? window.HULU_COMBO_REGISTRY : [];
  if (!REGISTRY.length) { console.log('[combo-cart] no registry — disabled'); return; }

  const JSONH = { 'Content-Type': 'application/json' };
  // POST /cart/update.js (empty) returns the FULL cart and isn't intercepted by the CLI preview
  // hot-reload shim (which only grabs GET *.js → HTML). Works preview + live.
  function getCart() { return fetch('/cart/update.js?t=' + Date.now(), { method: 'POST', headers: JSONH, body: '{}', credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json()); }
  function postCart(url, body) { return fetch(url, { method: 'POST', headers: JSONH, body: JSON.stringify(body), credentials: 'same-origin' }).then((r) => r.json()); }

  // ===== free-gift guard =====
  // A free-gift app (Kite: "Free Gift With Purchase") drops the gift into the cart as a normal
  // companion variant (e.g. the PUBLIC Charm) and zeroes it with an automatic discount. Our
  // reconciler would then see "a companion + an anchor present" and swap it to the NET variant —
  // which detaches Kite's discount (it's bound to the original variant) → the gift becomes $31.
  // A real combo companion is NEVER $0 (net price is $31/$20/$171), so a companion line whose
  // final_price is 0 can ONLY be the gift. Such lines are left fully alone: not claimed, not
  // counted toward anchor budget, not swapped — so Kite's discount keeps matching its variant.
  // We also honour an explicit gift property if the app sets one (belt-and-braces).
  function isFreeGift(line) {
    if (!line) return false;
    const p = line.properties || {};
    // Kite "Free Gift With Purchase" stamps every gift line with these AT ADD TIME, so they are
    // reliable no matter when the $0 discount materialises (final_price can lag a beat):
    //   _free_product:"true"   _rule_id:"free_gift_tier_N"   _kite_promo_name:"…"
    if (p._free_product === 'true' || p._free_product === true) return true;
    if (typeof p._rule_id === 'string' && /free.?gift/i.test(p._rule_id)) return true;
    if (p._kite_promo_name) return true;
    // Fallback: a combo companion is never $0 (net is $31/$20/$171), so any companion line that is
    // fully discounted to $0 can only be the gift.
    if (line.final_price === 0 || line.price === 0) return true;
    return false;
  }

  // ===== section rendering (repaint in place, never full reload) =====
  function currentSectionIds() {
    const ids = new Set(['cart-icon-bubble']);
    const ci = document.querySelector('cart-items, cart-drawer-items');
    if (ci && typeof ci.getSectionsToRender === 'function') {
      try { ci.getSectionsToRender().forEach((s) => ids.add(s.section)); } catch (_) {}
    }
    return Array.from(ids).join(',');
  }
  function applySections(sections) {
    if (!sections) return;
    const bubbleHtml = sections['cart-icon-bubble'];
    if (typeof bubbleHtml === 'string') {
      const live = document.getElementById('cart-icon-bubble');
      if (live) {
        const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const fresh = parsed.querySelector('#cart-icon-bubble') || parsed.querySelector('.shopify-section') || parsed.body;
        if (fresh) live.innerHTML = fresh.innerHTML;
      }
    }
    const ci = document.querySelector('cart-items, cart-drawer-items');
    if (ci && typeof ci.getSectionsToRender === 'function' && typeof ci.getSectionInnerHTML === 'function') {
      ci.getSectionsToRender().forEach((s) => {
        const html = sections[s.section];
        if (typeof html !== 'string') return; // null-guard
        const container = document.getElementById(s.id);
        if (!container) return;
        const target = container.querySelector(s.selector) || container;
        target.innerHTML = ci.getSectionInnerHTML(html, s.selector);
      });
    }
  }

  // Lock the WHOLE cart UI while we mutate, not just checkout: Dawn's native qty steppers /
  // remove buttons fire their own /cart/change.js outside our mutex, so a customer mashing them
  // mid-reconcile could overwrite our result. pointer-events:none on the cart sections closes
  // that window (the lock resets harmlessly when we re-render the sections at the end).
  let busyStyleInjected = false;
  function setCheckoutLocked(locked) {
    if (!busyStyleInjected) {
      const s = document.createElement('style');
      s.textContent = '.hulu-cart-busy cart-items,.hulu-cart-busy cart-drawer-items,.hulu-cart-busy .cart-items,.hulu-cart-busy .cart__footer,.hulu-cart-busy .cart__ctas,.hulu-cart-busy .quantity,.hulu-cart-busy cart-remove-button{pointer-events:none!important;}';
      document.head.appendChild(s);
      busyStyleInjected = true;
    }
    document.documentElement.classList.toggle('hulu-cart-busy', !!locked);
    document.querySelectorAll('[name="checkout"], #checkout, .cart__checkout-button, .shopify-payment-button').forEach((el) => {
      el.style.pointerEvents = locked ? 'none' : '';
      el.style.opacity = locked ? '0.45' : '';
      el.setAttribute('aria-busy', locked ? 'true' : 'false');
      if (el.tagName === 'BUTTON') el.toggleAttribute('disabled', locked);
    });
  }

  // ===== THE CORE: target absolute qty per (combo, colour), from the ORIGINAL cart =====
  // Returns [{ comboId, netVar, pubVar, net, pub }]. Totals come from the passed cart, so as long
  // as we pass the ORIGINAL (pre-add) cart, net/pub never double-count.
  // Companion lines are identified by VARIANT membership (net or public variant of a combo),
  // NOT by the _combo tag. This way a companion the CUSTOMER added themselves (untagged, e.g.
  // a plain $239 Astra) is still managed — it gets upgraded/downgraded like a tagged one,
  // instead of being orphaned alongside a tagged duplicate. The _combo tag is only a tie-break
  // for variants that belong to more than one combo. `claimed` stops two combos double-counting
  // the same line.
  function computeTargetsSimple(cart) {
    const targets = [];
    const claimed = new Set(); // line index → already assigned to a combo
    for (const combo of REGISTRY) {
      const anchorIds = combo.anchorProductIds || [];
      const anchorQty = cart.items.filter((l) => anchorIds.indexOf(l.product_id) !== -1).reduce((s, l) => s + l.quantity, 0);
      for (const comp of combo.companions || []) {
        const netToPublic = comp.netToPublic || {};
        const publicToNet = comp.publicToNet || {};
        const netIds = Object.keys(netToPublic).map(Number);
        const byColor = {};
        cart.items.forEach((l, i) => {
          if (claimed.has(i)) return;
          if (isFreeGift(l)) { console.log('[combo-cart] skip free-gift line — not swapping', l.variant_id, l.title || ''); return; }
          let netVar = null;
          if (netIds.indexOf(l.variant_id) !== -1) netVar = l.variant_id;
          else if (publicToNet[l.variant_id] != null) netVar = Number(publicToNet[l.variant_id]);
          if (netVar == null) return; // not a companion variant of this combo
          const tag = l.properties && l.properties._combo;
          if (tag && tag !== combo.id) return; // explicitly tagged for a DIFFERENT combo
          claimed.add(i);
          const g = byColor[netVar] || (byColor[netVar] = { netVar: netVar, pubVar: Number(netToPublic[netVar]), total: 0 });
          g.total += l.quantity;
        });
        let budget = anchorQty;
        Object.keys(byColor).forEach((k) => {
          const g = byColor[k];
          const net = Math.max(0, Math.min(g.total, budget));
          budget -= net;
          targets.push({ comboId: combo.id, netVar: g.netVar, pubVar: g.pubVar, net: net, pub: g.total - net });
        });
      }
    }
    return targets;
  }

  // ===== FUTURE-PROOF: cheapest-wins allocator (only runs when a companion is SHARED) =====
  // Active only if the same companion variant appears in >1 combo (i.e. same item, different
  // anchor, different price). Today no combo shares a companion → HAS_SHARED is false → we use
  // computeTargetsSimple (the proven path) and this code never runs. When a client later adds a
  // cross-price combo, HAS_SHARED flips true and allocation kicks in automatically.
  // ⚠️ Untested in production until the first shared-companion combo exists — verify on first use.
  const HAS_SHARED = (function () {
    const seen = {};
    for (const combo of REGISTRY) for (const comp of (combo.companions || [])) {
      for (const k of Object.keys(comp.publicToNet || {})) { seen[k] = (seen[k] || 0) + 1; if (seen[k] > 1) return true; }
    }
    return false;
  })();
  console.log('[combo-cart] HAS_SHARED=', HAS_SHARED, '→ path:', HAS_SHARED ? 'allocator' : 'simple');

  // Greedy capacity-aware allocation: tagged lines honour their combo (authoritative price);
  // untagged free-agent lines go to the eligible combo with the LOWEST net price (best discount
  // for the customer) that still has anchor budget. Reduces to the simple per-combo result when
  // no companion is shared, so the two paths agree in the non-overlap case.
  function computeTargetsAllocated(cart) {
    const remaining = {}; // (comboId#compIndex) → anchor budget left
    REGISTRY.forEach((c) => {
      const aq = cart.items.filter((l) => (c.anchorProductIds || []).indexOf(l.product_id) !== -1).reduce((s, l) => s + l.quantity, 0);
      (c.companions || []).forEach((comp, ci) => { remaining[c.id + '#' + ci] = aq; });
    });
    const lines = [];
    cart.items.forEach((l) => {
      if (isFreeGift(l)) { console.log('[combo-cart] skip free-gift line (alloc) — not swapping', l.variant_id, l.title || ''); return; } // never allocate/swap a free-gift line
      const cands = [];
      REGISTRY.forEach((c) => (c.companions || []).forEach((comp, ci) => {
        const n2p = comp.netToPublic || {}, p2n = comp.publicToNet || {}, np = comp.netPrices || {};
        let netVar = null, pubVar = null;
        if (n2p[l.variant_id] != null) { netVar = l.variant_id; pubVar = Number(n2p[l.variant_id]); }
        else if (p2n[l.variant_id] != null) { pubVar = l.variant_id; netVar = Number(p2n[l.variant_id]); }
        if (netVar != null) cands.push({ bucket: c.id + '#' + ci, comboId: c.id, netVar: netVar, pubVar: pubVar, price: np[netVar] != null ? Number(np[netVar]) : 0 });
      }));
      if (cands.length) lines.push({ qty: l.quantity, cands: cands, tag: (l.properties && l.properties._combo) || null });
    });
    const acc = {};
    const bump = (comboId, netVar, pubVar, dnet, dpub) => {
      const k = comboId + ':' + netVar;
      const g = acc[k] || (acc[k] = { comboId: comboId, netVar: netVar, pubVar: pubVar, net: 0, pub: 0 });
      g.net += dnet; g.pub += dpub;
    };
    lines.sort((a, b) => (a.tag ? 0 : 1) - (b.tag ? 0 : 1)).forEach((line) => {
      let pick = line.tag ? line.cands.find((c) => c.comboId === line.tag) : null;
      if (!pick) {
        const free = line.cands.filter((c) => (remaining[c.bucket] || 0) > 0).sort((a, b) => a.price - b.price);
        pick = free[0] || line.cands.slice().sort((a, b) => a.price - b.price)[0];
      }
      const avail = Math.max(0, remaining[pick.bucket] || 0);
      const net = Math.min(line.qty, avail);
      remaining[pick.bucket] = avail - net;
      bump(pick.comboId, pick.netVar, pick.pubVar, net, line.qty - net);
    });
    return Object.keys(acc).map((k) => acc[k]);
  }

  function computeTargets(cart) {
    return HAS_SHARED ? computeTargetsAllocated(cart) : computeTargetsSimple(cart);
  }

  // Count a variant's qty within a combo: include lines that are untagged OR tagged for THIS
  // combo (so the customer's own untagged companion line counts), but not lines tagged for a
  // different combo.
  function qtyOf(cart, comboId, vid) {
    return cart.items.filter((l) => l.variant_id === vid && (!l.properties || !l.properties._combo || l.properties._combo === comboId)).reduce((s, l) => s + l.quantity, 0);
  }

  // ===== reconcile: ONE atomic update.js sets each companion variant to its target qty =====
  // No tags → each companion variant is at most one cart line, so we address lines by variant_id.
  // /cart/update.js is an absolute-state setter: it ADDS an absent variant, REMOVES at qty 0, all
  // in a SINGLE request. That makes every swap ATOMIC — it either fully happens or not at all — so
  // an interrupted reconcile (customer navigates away mid-compute) can NEVER leave a phantom 2nd
  // unit. It's also fast: 1 round trip instead of the old add.js → re-read → update.js (3+).
  // cartArg (Dawn's cartUpdate cartData) is used directly when present — it has variant_id +
  // quantity + product_id, all we need — so a passive reconcile is often a single request total.
  async function reconcile(cartArg, render) {
    const cart = (cartArg && Array.isArray(cartArg.items)) ? cartArg : await getCart();
    const targets = computeTargets(cart);
    if (!targets.length) return;

    const updates = {};
    let changed = false;
    for (const t of targets) {
      if (qtyOf(cart, t.comboId, t.netVar) !== t.net) { updates[t.netVar] = t.net; changed = true; }
      if (qtyOf(cart, t.comboId, t.pubVar) !== t.pub) { updates[t.pubVar] = t.pub; changed = true; }
    }
    if (!changed) return; // already correct → no network at all

    console.log('[combo-cart] swap updates=', JSON.stringify(updates));
    setCheckoutLocked(true);
    // Atomic swap. If it fails (e.g. adding a public variant that's out of stock), the WHOLE call
    // is rejected → cart unchanged, checkout stays locked. No half-state, ever.
    const r = await postCart('/cart/update.js', { updates, sections: currentSectionIds(), sections_url: window.location.pathname });
    if (r && (r.status || r.description)) { console.warn('[combo-cart] swap failed (maybe out of stock) → cart unchanged, stay locked', r); return; }
    if (render) applySections(r.sections);
    setCheckoutLocked(false);
  }

  // ===== orchestrator: single writer, serialized via a promise chain (mutex) =====
  let chain = Promise.resolve();
  let isApplying = false;
  function enqueue(op) {
    chain = chain.then(async () => {
      isApplying = true;
      try { await op(); }
      catch (e) { console.warn('[combo-cart] op error', e); setCheckoutLocked(false); }
      finally { isApplying = false; }
    });
    return chain;
  }

  // Add-to-cart from a product page. `items` already tagged by bundle-product-form.
  // We add (carrying sections so the cart notification + icon update), then reconcile to fix quota.
  function addBundleIntent(items) {
    return enqueue(async () => {
      const notif = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const secIds = notif && typeof notif.getSectionsToRender === 'function'
        ? Array.from(new Set(notif.getSectionsToRender().map((s) => s.id).concat('cart-icon-bubble'))).join(',')
        : 'cart-icon-bubble';
      const r = await postCart('/cart/add.js', { items, sections: secIds, sections_url: window.location.pathname });
      if (r && (r.status || r.description)) { console.warn('[combo-cart] add-intent failed', r); return; }
      // Trigger Dawn's "added to cart" notification / drawer + header count.
      if (notif && typeof notif.renderContents === 'function') {
        try { if (!r.id && r.items && r.items[0]) r.key = r.items[0].key; notif.renderContents(r); } catch (_) { applySections(r.sections); }
      } else {
        applySections(r.sections);
      }
      // Then correct quota (e.g. companion qty > anchor) — usually a no-op for a clean combo add.
      await reconcile(null, true);
    });
  }

  // passive trigger: Dawn-native qty/remove on cart page or drawer
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (isApplying) return;
      const cd = event && event.cartData;
      enqueue(() => reconcile(cd && typeof cd.item_count === 'number' && Array.isArray(cd.items) ? cd : null, true));
    });
  }

  // navigation fuse: never let an un-reconciled cart reach the cart page OR checkout.
  // The add-to-cart notification shows instantly (Dawn), but the price-swap reconcile is still
  // running in the background. If the user clicks "View cart" / "Check out" before it finishes,
  // they'd land on a full-price cart. So we HOLD the navigation until the mutex queue drains
  // (reconcile done), then go. Covers checkout buttons + any link to the /cart page.
  function cartHref(link) {
    if (!link) return null;
    try { return new URL(link.href, window.location.origin).pathname.replace(/\/$/, '').endsWith('/cart') ? link.href : null; }
    catch (_) { return null; }
  }
  document.addEventListener('click', (ev) => {
    const checkoutBtn = ev.target.closest('[name="checkout"], #checkout, .cart__checkout-button, .shopify-payment-button button');
    const toCart = checkoutBtn ? null : cartHref(ev.target.closest('a[href]'));
    if (!checkoutBtn && !toCart) return;
    const dest = checkoutBtn ? '/checkout' : toCart;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    enqueue(() => reconcile(null, false)).then(() => { window.location.href = dest; });
  }, true);

  // run once on load (heals a cart left bad from a previous session)
  enqueue(() => reconcile(null, true));

  window.HuluComboCart = { addBundleIntent, reconcile: () => enqueue(() => reconcile(null, true)) };
})();
